import { useState, useEffect, useRef } from "react";
import { Lock, Calendar, User, Clock, CheckCircle2, AlertCircle, AlertTriangle, Users, Shield, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { UserPresence } from "@/hooks/useSessionPresence";
import { useCommitBlocker } from "@/hooks/useCommitBlocker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface DecisionCommitData {
  committed_option_id: string;
  committed_option_name: string;
  validity_days: 30 | 60 | 90;
  owner_name: string;
  review_date: string;
  excluded_options?: string[];
  forced_commit?: boolean;
  forced_commit_active_users?: string[];
}

interface TradeOff {
  optionName: string;
  criterion: string;
  impact: string;
}

interface ExcludedOption {
  id: string;
  name: string;
  consequence: string;
  isDelayOption?: boolean;
}

interface DecisionCommitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dominantOptionId: string;
  dominantOptionName: string;
  sessionId: string;
  workspaceId: string;
  reportId?: string;
  reportVersion?: number;
  tradeOffs?: TradeOff[];
  excludedOptions?: ExcludedOption[];
  activeUsers?: UserPresence[];
  onCommit: (data: DecisionCommitData) => Promise<void>;
  alignmentScore?: number;
  thesisSummary?: string;
  koStatus?: { criterion: string; passed: boolean; allOptionsFailed?: boolean; passingOptions?: string[] };
  optionScores?: Array<{ name: string; score: number; isKoFailed: boolean }>;
}

export const DecisionCommitDialog = ({
  open,
  onOpenChange,
  dominantOptionId,
  dominantOptionName,
  sessionId,
  workspaceId,
  reportId,
  reportVersion,
  tradeOffs = [],
  excludedOptions = [],
  activeUsers = [],
  onCommit,
  alignmentScore,
  thesisSummary,
  koStatus,
  optionScores,
}: DecisionCommitDialogProps) => {
  const [validityDays, setValidityDays] = useState<30 | 60 | 90>(30);
  const [ownerName, setOwnerName] = useState("");
  const [tradeOffAcknowledged, setTradeOffAcknowledged] = useState(false);
  const [exclusionsAcknowledged, setExclusionsAcknowledged] = useState(false);
  const [koAcknowledged, setKoAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceCommit, setForceCommit] = useState(false);
  const ownerInputRef = useRef<HTMLInputElement>(null);

  const { canCommit: noActiveEditors, activeEditors, warningMessage } = useCommitBlocker(activeUsers);

  const koNeedsAck = koStatus && !koStatus.passed;

  useEffect(() => {
    if (open) {
      setTimeout(() => ownerInputRef.current?.focus(), 100);
      setTradeOffAcknowledged(tradeOffs.length === 0);
      setExclusionsAcknowledged(excludedOptions.length === 0);
      setKoAcknowledged(!koNeedsAck);
      setForceCommit(false);
    }
  }, [open, tradeOffs.length, excludedOptions.length, koNeedsAck]);

  const calculateReviewDate = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const [reviewDate, setReviewDate] = useState(calculateReviewDate(30));

  const handleValidityChange = (value: string) => {
    const days = parseInt(value) as 30 | 60 | 90;
    setValidityDays(days);
    setReviewDate(calculateReviewDate(days));
  };

  const handleCommit = async () => {
    if (!ownerName.trim()) return;
    if (tradeOffs.length > 0 && !tradeOffAcknowledged) return;
    if (excludedOptions.length > 0 && !exclusionsAcknowledged) return;
    
    setIsSubmitting(true);
    try {
      const commitPayload: DecisionCommitData = {
        committed_option_id: dominantOptionId,
        committed_option_name: dominantOptionName,
        validity_days: validityDays,
        owner_name: ownerName.trim(),
        review_date: reviewDate,
        excluded_options: excludedOptions.map(o => o.name),
      };

      if (forceCommit && activeEditors.length > 0) {
        commitPayload.forced_commit = true;
        commitPayload.forced_commit_active_users = activeEditors.map(u => u.user_id);
      }

      await onCommit(commitPayload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = ownerName.trim().length > 0 && 
    (tradeOffs.length === 0 || tradeOffAcknowledged) &&
    (excludedOptions.length === 0 || exclusionsAcknowledged) &&
    (!koNeedsAck || koAcknowledged) &&
    (noActiveEditors || forceCommit);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const realDecisions = excludedOptions.filter(o => !o.isDelayOption);
  const delayOptions = excludedOptions.filter(o => o.isDelayOption);

  const hasStrategicContext = thesisSummary || alignmentScore != null || koStatus;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0">
        {/* Strategic Context Header */}
        <div className="bg-gradient-to-br from-primary/8 via-primary/4 to-transparent border-b border-primary/10 px-6 pt-6 pb-5">
          <DialogHeader className="text-left mb-0">
            <DialogTitle className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                <Lock className="h-4 w-4 text-primary" />
              </div>
              Verbindlicher Commit
            </DialogTitle>
          </DialogHeader>

          {hasStrategicContext && (
            <div className="mt-4 space-y-3">
              {/* Thesis */}
              {thesisSummary && (
                <p className="text-sm italic text-muted-foreground font-serif leading-relaxed border-l-2 border-primary/30 pl-3">
                  „{thesisSummary}"
                </p>
              )}

              {/* Alignment + K.O. Row */}
              <div className="flex flex-wrap items-center gap-3">
                {alignmentScore != null && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background/80 border border-border/50">
                    <TrendingUp className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs text-muted-foreground">Alignment</span>
                    <span className="text-lg font-bold font-mono text-primary tabular-nums">{alignmentScore}</span>
                  </div>
                )}

                {koStatus && koStatus.passed && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="text-xs font-medium">
                      K.O.: {koStatus.criterion}
                    </span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      Bestanden
                    </Badge>
                  </div>
                )}
              </div>

              {/* Mini Score Table */}
              {optionScores && optionScores.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {optionScores.map((opt) => (
                    <div 
                      key={opt.name}
                      className={cn(
                        "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs",
                        opt.name === dominantOptionName
                          ? "bg-primary/10 border border-primary/20 font-medium"
                          : "bg-muted/50 text-muted-foreground"
                      )}
                    >
                      <span className="truncate max-w-[120px]">{opt.name}</span>
                      <span className="font-mono font-semibold tabular-nums">{opt.score.toFixed(1)}</span>
                      {opt.isKoFailed && <AlertTriangle className="h-3 w-3 text-destructive" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Scrollable Content */}
        <div className="space-y-5 px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {/* Selected Option */}
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-0.5">
              Verbindlicher Fokus
            </p>
            <p className="font-semibold text-primary">
              {dominantOptionName}
            </p>
          </div>

          {/* K.O. Not Passed — Contextual Acknowledgment */}
          {koNeedsAck && koStatus && (
            <div className={cn(
              "p-3 rounded-lg border transition-colors",
              koAcknowledged
                ? "bg-muted/50 border-border"
                : "bg-destructive/5 border-destructive/20"
            )}>
              <div className="flex items-start gap-2 mb-2">
                {koAcknowledged ? (
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                )}
                <p className="text-sm font-medium">
                  {koAcknowledged ? "K.O.-Abweichung akzeptiert" : "K.O.-Kriterium nicht voll erfüllt"}
                </p>
              </div>
              <div className="text-xs text-muted-foreground space-y-2 mb-3">
                <p>
                  <strong className="text-foreground">{dominantOptionName}</strong> erreicht beim K.O.-Kriterium 
                  „{koStatus.criterion}" nicht die höchste Stufe.
                </p>
                {koStatus.allOptionsFailed ? (
                  <p>
                    <strong>Keine</strong> der verfügbaren Optionen erfüllt dieses K.O.-Kriterium vollständig. 
                    Das System empfiehlt dennoch die strategisch stabilste Option.
                  </p>
                ) : koStatus.passingOptions && koStatus.passingOptions.length > 0 ? (
                  <p>
                    {koStatus.passingOptions.length === 1 ? "Option" : "Optionen"}{" "}
                    <strong>{koStatus.passingOptions.join(", ")}</strong>{" "}
                    {koStatus.passingOptions.length === 1 ? "erfüllt" : "erfüllen"} das K.O.-Kriterium, 
                    {koStatus.passingOptions.length === 1 ? " wurde" : " wurden"} aber aufgrund anderer strategischer Faktoren nicht empfohlen.
                  </p>
                ) : null}
                <p className="italic">
                  Die Empfehlung basiert auf der Gesamtbewertung aus Risikoprofil, Alignment und Deadline-Konsistenz — nicht allein auf dem K.O.-Score.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="ko-ack"
                  checked={koAcknowledged}
                  onCheckedChange={(checked) => setKoAcknowledged(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="ko-ack" className="text-xs cursor-pointer leading-relaxed">
                  Ich verstehe, dass das K.O.-Kriterium nicht voll erfüllt ist, und akzeptiere die Empfehlung bewusst.
                </label>
              </div>
            </div>
          )}

          {/* Explicit Exclusion Acknowledgment */}
          {excludedOptions.length > 0 && (
            <div className={cn(
              "p-3 rounded-lg border transition-colors",
              exclusionsAcknowledged 
                ? "bg-muted/50 border-border" 
                : "bg-destructive/5 border-destructive/20"
            )}>
              <div className="flex items-start gap-2 mb-2">
                {exclusionsAcknowledged ? (
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                )}
                <p className="text-sm font-medium">
                  {exclusionsAcknowledged ? "Verzicht bestätigt" : "Bewusster Verzicht"}
                </p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Mit diesem Commit verzichtest du für {validityDays} Tage bewusst auf:
              </p>
              
              {realDecisions.length > 0 && (
                <ul className="space-y-2 mb-2">
                  {realDecisions.map((option, idx) => (
                    <li key={idx} className="text-xs flex items-start gap-1.5 p-2 bg-background rounded border border-border/50">
                      <span className="font-medium text-foreground shrink-0">{option.name}:</span>
                      <span className="text-muted-foreground">{option.consequence}</span>
                    </li>
                  ))}
                </ul>
              )}

              {delayOptions.length > 0 && (
                <ul className="space-y-2 mb-3">
                  {delayOptions.map((option, idx) => (
                    <li key={idx} className="text-xs flex items-start gap-1.5 p-2 bg-muted/30 rounded border border-dashed border-muted-foreground/30">
                      <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground/60 mt-0.5 shrink-0" />
                      <div>
                        <span className="font-medium text-muted-foreground">{option.name}</span>
                        <span className="text-muted-foreground/80 italic ml-1">(Entscheidungsaufschub)</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {realDecisions.length > 0 && delayOptions.length === 0 && <div className="mb-3" />}

              <div className="flex items-start gap-2">
                <Checkbox 
                  id="exclusion-ack"
                  checked={exclusionsAcknowledged}
                  onCheckedChange={(checked) => setExclusionsAcknowledged(checked === true)}
                  className="mt-0.5"
                />
                <label 
                  htmlFor="exclusion-ack" 
                  className="text-xs cursor-pointer leading-relaxed"
                >
                  Ich verstehe, dass dieser Verzicht <strong>bewusst</strong> ist und akzeptiere die Konsequenzen.
                </label>
              </div>
            </div>
          )}

          {/* Trade-off Acknowledgment */}
          {tradeOffs.length > 0 && (
            <div className={cn(
              "p-3 rounded-lg border transition-colors",
              tradeOffAcknowledged 
                ? "bg-emerald-50/50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800" 
                : "bg-amber-50/50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800"
            )}>
              <div className="flex items-start gap-2 mb-2">
                {tradeOffAcknowledged ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                )}
                <p className="text-sm font-medium">
                  {tradeOffAcknowledged ? "Trade-offs bestätigt" : "Akzeptierte Trade-offs"}
                </p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Du hast bewusst akzeptiert, dass {dominantOptionName}:
              </p>
              <ul className="space-y-1.5 mb-3">
                {tradeOffs.map((tradeOff, idx) => (
                  <li key={idx} className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <span className="text-muted-foreground/60">•</span>
                    <span>{tradeOff.impact}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-start gap-2">
                <Checkbox 
                  id="trade-off-ack"
                  checked={tradeOffAcknowledged}
                  onCheckedChange={(checked) => setTradeOffAcknowledged(checked === true)}
                  className="mt-0.5"
                />
                <label 
                  htmlFor="trade-off-ack" 
                  className="text-xs cursor-pointer leading-relaxed"
                >
                  Ich bestätige, dass dies <strong>bewusste</strong> Trade-offs sind, nicht übersehene Risiken.
                </label>
              </div>
            </div>
          )}

          {/* Decision Owner */}
          <div className="space-y-1.5">
            <Label htmlFor="owner" className="flex items-center gap-1.5 text-sm">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              Entscheidungsverantwortung
            </Label>
            <Input
              ref={ownerInputRef}
              id="owner"
              placeholder="Name eingeben..."
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              className={cn(
                "transition-all",
                !ownerName.trim() && "ring-2 ring-primary/20 focus:ring-primary/40"
              )}
            />
            <p className="text-[11px] text-muted-foreground">
              Diese Person wird im Report als Verantwortliche genannt.
            </p>
          </div>

          {/* Validity Period */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-sm">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              Gültigkeit
            </Label>
            <RadioGroup 
              value={validityDays.toString()} 
              onValueChange={handleValidityChange}
              className="grid grid-cols-3 gap-2"
            >
              {[30, 60, 90].map((days) => (
                <div 
                  key={days}
                  className={cn(
                    "border rounded-lg p-2.5 cursor-pointer transition-all text-center",
                    validityDays === days 
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30" 
                      : "border-border hover:border-primary/40"
                  )}
                >
                  <RadioGroupItem value={days.toString()} id={`${days}days`} className="sr-only" />
                  <label htmlFor={`${days}days`} className="cursor-pointer block">
                    <span className="text-base font-semibold">{days}</span>
                    <span className="text-xs text-muted-foreground block">Tage</span>
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Review Date */}
          <div className="flex items-center gap-2 p-2.5 bg-muted/50 rounded-lg">
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="text-sm">
              <span className="text-muted-foreground">Review am </span>
              <span className="font-medium">{formatDate(reviewDate)}</span>
            </div>
          </div>

          {/* Commit Blocker Warning */}
          {!noActiveEditors && (
            <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
              <div className="flex items-start gap-2 mb-2">
                <Users className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Aktive Bearbeitung</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{warningMessage}</p>
                </div>
              </div>
              <div className="flex items-start gap-2 mt-2">
                <Checkbox
                  id="force-commit"
                  checked={forceCommit}
                  onCheckedChange={(checked) => setForceCommit(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="force-commit" className="text-xs cursor-pointer leading-relaxed">
                  Trotzdem committen – ich übernehme die Verantwortung.
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Sticky Commit Footer */}
        <div className="shrink-0 px-6 py-4 border-t bg-background/95 backdrop-blur">
          <div className="flex items-center justify-between gap-3">
            <Button 
              variant="ghost" 
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground"
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleCommit}
              disabled={!isValid || isSubmitting}
              className="gap-2 min-w-[180px] shadow-md"
              size="lg"
            >
              <Lock className="h-4 w-4" />
              {isSubmitting ? "Wird gespeichert..." : "Verbindlich committen"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
